import {
  ApeironBlueButton,
  INFO_POPUP_TYPE,
  InfoPopup,
  Pagination
} from '@apeiron/temp'
import {
  Box,
  ButtonBase,
  Stack,
  styled,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { RentalSettingFieldValues } from '@src/components/orbitalTrack/rentalSetting/common/RentalSettingDialog'
import { ErrorMessages } from '@src/components/orbitalTrack/rentalSetting/inviteRental/ErrorMessages'
import ApeironBorderButton from '@src/components/share/apeiron/ApeironBorderButton'
import ApeironConfirmButton from '@src/components/share/apeiron/ApeironConfirmButton'
import ApeironWalletAddressInput from '@src/components/share/apeiron/ApeironWalletAddressInput'
import NumberInputWithControl from '@src/components/share/generic/NumberInputWithControl'
import {
  FormEventHandler,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState
} from 'react'
import {
  Control,
  Controller,
  FieldValues,
  useFieldArray,
  useForm,
  UseFormReturn
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { isAddress } from 'viem'

const EditButton = styled(ApeironBorderButton)`
  border: 2px solid #818181;
  font-weight: 300;
  width: 100%;
  ${props => props.theme.breakpoints.down('res480')} {
    font-size: 14px;
    padding: 0px 10px;
  }
`

const NewAddressButton = styled(ApeironBlueButton)`
  font-weight: 300;
  height: 100%;
  ${props => props.theme.breakpoints.down('res480')} {
    font-size: 14px;
    padding: 0px 10px;
  }
`

const DeleteButton = styled(ButtonBase)`
  color: rgba(196, 196, 196, 0.6);
  :hover {
    color: #f00;
  }
`

const Index = styled(Typography)`
  color: #fff;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  width: 100%;
  text-align: center;
`

const Fields = Stack

const Upper = styled(Box)`
  background: #262829;
  padding: 0px 15px;
`

const ContentContainer = styled(Box)`
  padding: 0px 0px;
  padding-bottom: 20px;
  background-color: #171719;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`

const ContentHeader = styled(Box)`
  padding: 20px 15px;
`

const NewAddressForm = styled('form')`
  background: rgba(255, 191, 54, 0.2);
`

const AddressRow = styled(Box)`
  display: grid;
  align-items: center;
  padding: 5px 9px;
  column-gap: 6px;
  grid-auto-flow: column;
  grid-template-columns: 30px 1fr 65px;
`

const NewText = styled(Typography)`
  width: 30px;
  color: #ffbf39;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  text-transform: uppercase;
`

const Popup = styled(InfoPopup)`
  width: 96%;
  max-width: 600px;
  /* min-height: 400px; */
`

const PopupInner = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const Title = styled(Typography)`
  font-size: 20px;
`

const TrackPerAddressInput = styled(NumberInputWithControl)`
  width: 200px;
`

const ErrorMessage = styled(Typography)`
  color: #d32f2f;
  font-size: 0.75rem;
  text-align: right;
`

const TrackPerAddressContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 10px;
  margin-top: 20px;
  margin-bottom: 20px;
`

const ConfirmButton = ApeironConfirmButton

const ButtonContainer = styled(Box)`
  padding: 12px 15px;
  display: flex;
  justify-content: center;
`

const MOBILE_PAGE_SIZE = 4

const DESKTOP_PAGE_SIZE = 8

const WhitelistEdit = (props: Props) => {
  const { disabled, form: parentForm } = props

  const {
    control,
    getValues,
    reset,
    formState: { errors, isValid },
    setError,
    clearErrors,
    trigger: triggerValidation
  } = useForm<WhitelistFormValues>({
    mode: 'onChange',
    values: {
      whitelistAddresses: parentForm.getValues().whitelistAddresses,
      tracksPerAddress: parentForm.getValues().tracksPerAddress
    }
  })

  const [modalOpen, setModalOpen] = useState(false)

  const [newAddress, setNewAddress] = useState('')

  const { t } = useTranslation()

  const handleOpenModal = () => {
    setModalOpen(true)
  }

  const handleClose = useCallback(() => {
    setModalOpen(false)
    setNewAddress('')
    parentForm.trigger('whitelistAddresses')
    parentForm.trigger('tracksPerAddress')
    reset({
      whitelistAddresses: parentForm.getValues().whitelistAddresses,
      tracksPerAddress: parentForm.getValues().tracksPerAddress
    })
  }, [parentForm])

  const handleConfirm = () => {
    parentForm.setValue('whitelistAddresses', getValues().whitelistAddresses, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    })
    parentForm.setValue('tracksPerAddress', getValues().tracksPerAddress, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    })
    setModalOpen(false)
  }

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'whitelistAddresses',
    rules: {
      required: {
        value: true,
        message: t(
          'godiverse.orbital_track.invite_whitelist.errors.min_addresses'
        )
      }
    }
  })

  const [currentPage, setCurrentPage] = useState(0)

  const theme = useTheme()

  const mobile = useMediaQuery(theme.breakpoints.down('res480'))

  const pageSize = mobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE

  const totalPage = Math.max(1, Math.ceil(fields.length / pageSize))

  const currentFields = fields.filter(
    (_, index) =>
      index >= currentPage * pageSize && index < (currentPage + 1) * pageSize
  )

  const handleSubmitNewAddress: FormEventHandler<HTMLFormElement> = useCallback(
    event => {
      event.preventDefault()
      event.stopPropagation()
      append({ value: newAddress })
      setNewAddress('')
    },
    [newAddress, append]
  )

  const handleAddressChange = useCallback((address: string) => {
    setNewAddress(address)
  }, [])

  useLayoutEffect(() => {
    if (currentPage + 1 > totalPage) {
      setCurrentPage(totalPage - 1)
    }
  }, [currentPage, totalPage])

  useEffect(() => {
    if (newAddress) {
      setError('newAddress', {
        type: 'manual',
        message: t(
          'godiverse.orbital_track.invite_whitelist.errors.unsaved_input',
          {
            input: newAddress
          }
        )
      })
    } else {
      clearErrors('newAddress')
      triggerValidation()
    }
  }, [newAddress])

  return (
    <>
      <EditButton disabled={disabled} onClick={handleOpenModal}>
        {t('common.button.edit')}
      </EditButton>
      <ErrorMessage>
        {parentForm.formState.errors.whitelistAddresses?.message}
      </ErrorMessage>
      <ErrorMessage>
        {parentForm.formState.errors.tracksPerAddress?.message}
      </ErrorMessage>
      <Popup
        open={modalOpen}
        onClose={handleClose}
        type={INFO_POPUP_TYPE.BLACK}
      >
        <PopupInner>
          <Upper>
            <Title>
              {t(
                'godiverse.orbital_track.whitelist_popup.whitelist_wallet_address'
              )}
            </Title>
            <TrackPerAddressContainer>
              {t('godiverse.orbital_track.whitelist_popup.tracks')}
              <TrackPerAddressInput
                name='tracksPerAddress'
                control={control as unknown as Control<FieldValues>}
                defaultValue={1}
                rules={{
                  required: true,
                  min: {
                    value: 1,
                    message: t(
                      'godiverse.orbital_track.invite_whitelist.errors.min_tracks'
                    )
                  },
                  max: {
                    value: parentForm.getValues().rentTrackCount,
                    message: t(
                      'godiverse.orbital_track.invite_whitelist.errors.max_tracks'
                    )
                  }
                }}
              />
            </TrackPerAddressContainer>
          </Upper>

          <ContentContainer>
            <ContentHeader>
              <Typography>
                {t(
                  'godiverse.orbital_track.whitelist_popup.whitelist_wallet_address'
                )}
              </Typography>
            </ContentHeader>
            <Box>
              <NewAddressForm onSubmit={handleSubmitNewAddress}>
                <AddressRow>
                  <NewText>{t('new')}</NewText>
                  <ApeironWalletAddressInput
                    value={newAddress}
                    onChangeAddress={handleAddressChange}
                    placeholder={t(
                      'godiverse.orbital_track.whitelist_popup.add_new'
                    )}
                  />
                  {newAddress && (
                    <NewAddressButton
                      type='submit'
                      disabled={!isAddress(newAddress)}
                    >
                      {t('common.button.ok')}
                    </NewAddressButton>
                  )}
                </AddressRow>
              </NewAddressForm>
            </Box>
            <Fields>
              {currentFields.map((field, index) => {
                const offsetedIndex = currentPage * pageSize + index

                return (
                  <AddressRow key={field.id}>
                    <Index>{offsetedIndex + 1}</Index>
                    <Controller
                      render={({ field }) => (
                        <ApeironWalletAddressInput {...field} />
                      )}
                      rules={{
                        validate: value => {
                          if (!isAddress(value)) {
                            return t(
                              'godiverse.orbital_track.invite_whitelist.errors.invalid_input'
                            )
                          }

                          return true
                        }
                      }}
                      name={`whitelistAddresses.${offsetedIndex}.value`}
                      control={control}
                    />
                    <DeleteButton onClick={() => remove(offsetedIndex)}>
                      {t('common.button.delete')}
                    </DeleteButton>
                  </AddressRow>
                )
              })}
              {totalPage > 1 && (
                <Pagination
                  totalPage={totalPage}
                  value={currentPage + 1}
                  onPrev={() => setCurrentPage(cur => cur - 1)}
                  onNext={() => setCurrentPage(cur => cur + 1)}
                  prevDisabled={currentPage === 0}
                  nextDisabled={currentPage + 1 === totalPage}
                  onSubmit={pageNumber => {
                    if (pageNumber <= totalPage && pageNumber >= 1) {
                      setCurrentPage(pageNumber - 1)
                    }
                  }}
                />
              )}
            </Fields>
            <ButtonContainer>
              <ConfirmButton onClick={handleConfirm} disabled={!isValid} />
            </ButtonContainer>
            <ErrorMessages errors={errors} />
          </ContentContainer>
        </PopupInner>
      </Popup>
    </>
  )
}

type Props = {
  disabled: boolean
  form: UseFormReturn<RentalSettingFieldValues>
}

type WhitelistFormValues = FieldValues & {
  whitelistAddresses: { value: string }[]
  tracksPerAddress: number
}

export default WhitelistEdit
